import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Type, Info } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface RichTextBlockData {
  html: string;
  align: 'left' | 'center' | 'right';
}

interface RichTextBlockProps {
  data: RichTextBlockData;
  onChange: (data: RichTextBlockData) => void;
  onDelete?: () => void;
  isPreview?: boolean;
}

const RichTextBlock: React.FC<RichTextBlockProps> = ({ data, onChange, isPreview = false }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarColor, setToolbarColor] = useState('#000000');

  useEffect(() => {
    if (editorRef.current && data.html) {
      editorRef.current.innerHTML = data.html;
    }
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      onChange({
        ...data,
        html: editorRef.current.innerHTML
      });
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      onChange({
        ...data,
        html: editorRef.current.innerHTML
      });
    }
  };

  const setFontSize = (size: string) => {
    execCommand('fontSize', '7');
    const fontElements = editorRef.current?.querySelectorAll('font[size="7"]');
    fontElements?.forEach((element) => {
      const el = element as HTMLElement;
      const span = document.createElement('span');
      span.style.fontSize = size;
      // execCommand can merge foreColor's <font color="..."> and this size command onto the SAME
      // <font> tag — carry that color over (attribute or inline style) so it isn't lost when we
      // discard the original element below.
      const existingColor = el.getAttribute('color') || el.style.color;
      if (existingColor) span.style.color = existingColor;
      span.innerHTML = element.innerHTML;
      element.parentNode?.replaceChild(span, element);
    });
    handleInput();
  };

  const setTextColor = (color: string) => {
    execCommand('foreColor', color);
  };

  const updateAlign = (align: 'left' | 'center' | 'right') => {
    onChange({ ...data, align });
  };

  // If in preview mode, just show the formatted text
  if (isPreview) {
    return (
      <div
        className="prose max-w-none"
        style={{ textAlign: data.align }}
        dangerouslySetInnerHTML={{ __html: data.html }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-semibold text-gray-900">ข้อความ</h3>
        <div className="relative">
          <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
          <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
            <p className="font-semibold mb-1">เขียนข้อความอธิบาย</p>
            <p className="text-gray-300">เพิ่มข้อความอธิบายสินค้าหรือบริการ พร้อมเครื่องมือจัดรูปแบบตัวหนา ตัวเอียง สี และขนาดตัวอักษร</p>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <div className="flex items-center gap-1 border-r border-gray-300 pr-2">
            <button
              onClick={() => execCommand('bold')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="ตัวหนา"
              type="button"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => execCommand('italic')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="ตัวเอียง"
              type="button"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => execCommand('underline')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="ขีดเส้นใต้"
              type="button"
            >
              <Underline className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 border-r border-gray-300 pr-2">
            <Type className="w-4 h-4 text-gray-600" />
            <select
              onChange={(e) => setFontSize(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-sm"
              defaultValue="16px"
            >
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="24px">24px</option>
              <option value="28px">28px</option>
              <option value="32px">32px</option>
              <option value="36px">36px</option>
              <option value="48px">48px</option>
            </select>
          </div>

          <div className="flex items-center gap-2 border-r border-gray-300 pr-2">
            <label className="text-sm text-gray-600">สี:</label>
            <ColorSwatchPicker
              compact
              color={toolbarColor}
              onChange={(hex) => { setToolbarColor(hex); setTextColor(hex); }}
            />
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => updateAlign('left')}
              className={`p-2 rounded transition-colors ${data.align === 'left' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}
              title="ชิดซ้าย"
              type="button"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateAlign('center')}
              className={`p-2 rounded transition-colors ${data.align === 'center' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}
              title="กลาง"
              type="button"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateAlign('right')}
              className={`p-2 rounded transition-colors ${data.align === 'right' ? 'bg-blue-500 text-white' : 'hover:bg-gray-200'}`}
              title="ชิดขวา"
              type="button"
            >
              <AlignRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onFocus={() => setShowToolbar(true)}
          className="min-h-[150px] p-4 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          style={{ textAlign: data.align }}
          suppressContentEditableWarning
        >
          {!data.html && <span className="text-gray-400">พิมพ์ข้อความที่นี่... เลือกข้อความเพื่อตั้งค่าสี ขนาด และความหนา</span>}
        </div>
      </div>

      {data.html && (
        <div className="border-t pt-4">
          <p className="text-xs text-gray-500 mb-2">ตัวอย่าง:</p>
          <div
            className="p-4 bg-gray-50 rounded-lg"
            style={{ textAlign: data.align }}
            dangerouslySetInnerHTML={{ __html: data.html }}
          />
        </div>
      )}
    </div>
  );
};

export default RichTextBlock;
